import type { CSSProperties } from 'react';

/** 与 `docs/.../知识库.md` 中 `meta.background` 约定一致 */
export type KnowledgeBaseMetaBackgroundMode = 'none' | 'color' | 'gradient' | 'image';

export type KnowledgeBaseMetaBackgroundImageFit =
  | 'cover'
  | 'contain'
  | 'fill'
  | 'repeat'
  | 'no-repeat';

export interface KnowledgeBaseMetaBackground {
  mode: KnowledgeBaseMetaBackgroundMode;
  color?: string;
  gradient?: string;
  imageUrl?: string;
  imageFit?: KnowledgeBaseMetaBackgroundImageFit;
  imagePosition?: string;
}

/** 文档头图槽位 宽:高（21:9），与选图裁切、列表缩略图与编辑器一致 */
export const KNOWLEDGE_BASE_HEADER_IMAGE_DISPLAY_ASPECT_RATIO = 21 / 9;

/** 与 `docs/.../知识库.md` 中 `meta.headerImage` 一致；归一化裁切与 `ImageCropper` 输出对齐 */
export interface KnowledgeBaseMetaHeaderImageCropNorm {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface KnowledgeBaseMetaHeaderImage {
  src: string;
  cropNorm?: KnowledgeBaseMetaHeaderImageCropNorm | null;
  displayAspectRatio?: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/** 从 `meta` JSON 解析 `background`；无法识别时返回 `null`（按 none 处理） */
export function parseKnowledgeBaseMetaBackgroundFromMeta(meta: unknown): KnowledgeBaseMetaBackground | null {
  if (!isRecord(meta)) return null;
  const raw = meta.background;
  if (raw == null) return null;
  if (!isRecord(raw)) return null;
  const mode = raw.mode;
  if (mode !== 'none' && mode !== 'color' && mode !== 'gradient' && mode !== 'image') return null;
  const imageFit = raw.imageFit;
  const fitOk =
    imageFit === undefined ||
    imageFit === 'cover' ||
    imageFit === 'contain' ||
    imageFit === 'fill' ||
    imageFit === 'repeat' ||
    imageFit === 'no-repeat';
  if (!fitOk) return null;

  return {
    mode,
    color: typeof raw.color === 'string' ? raw.color : undefined,
    gradient: typeof raw.gradient === 'string' ? raw.gradient : undefined,
    imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : undefined,
    imageFit: imageFit as KnowledgeBaseMetaBackgroundImageFit | undefined,
    imagePosition: typeof raw.imagePosition === 'string' ? raw.imagePosition : undefined,
  };
}

function isFullFrameHeaderCropNorm(c: KnowledgeBaseMetaHeaderImageCropNorm): boolean {
  return c.x <= 1e-4 && c.y <= 1e-4 && c.w >= 1 - 1e-4 && c.h >= 1 - 1e-4;
}

function parseHeaderImageCropNorm(raw: unknown): KnowledgeBaseMetaHeaderImageCropNorm | null {
  if (!isRecord(raw)) return null;
  const { x, y, w, h } = raw;
  if (typeof x !== 'number' || typeof y !== 'number' || typeof w !== 'number' || typeof h !== 'number') return null;
  if (!Number.isFinite(x + y + w + h)) return null;
  if (x < -1e-3 || y < -1e-3 || w <= 0 || h <= 0 || x + w > 1 + 1e-3 || y + h > 1 + 1e-3) return null;
  return { x, y, w, h };
}

/** 从 `meta` 解析 `headerImage`；无效则返回 `null` */
export function parseKnowledgeBaseMetaHeaderImageFromMeta(meta: unknown): KnowledgeBaseMetaHeaderImage | null {
  if (!isRecord(meta)) return null;
  const raw = meta.headerImage;
  if (raw == null) return null;
  if (!isRecord(raw)) return null;
  const src = raw.src;
  if (typeof src !== 'string' || !src.trim()) return null;
  const parsedNorm = parseHeaderImageCropNorm(raw.cropNorm);
  const cropNorm =
    parsedNorm != null && !isFullFrameHeaderCropNorm(parsedNorm) ? parsedNorm : null;
  const dar = raw.displayAspectRatio;
  const displayAspectRatio =
    typeof dar === 'number' && Number.isFinite(dar) && dar > 0 ? dar : undefined;
  return {
    src: src.trim(),
    cropNorm,
    displayAspectRatio,
  };
}

/** 写入 `meta.headerImage` 前的 JSON 形状（整图不写 `cropNorm`） */
export function normalizeMetaHeaderImageForSave(
  h: KnowledgeBaseMetaHeaderImage,
  defaultDisplayAspectRatio?: number,
): Record<string, unknown> {
  const src = h.src.trim();
  const out: Record<string, unknown> = { src };
  if (h.cropNorm != null && !isFullFrameHeaderCropNorm(h.cropNorm)) {
    const c = h.cropNorm;
    out.cropNorm = { x: c.x, y: c.y, w: c.w, h: c.h };
  }
  const dar = h.displayAspectRatio ?? defaultDisplayAspectRatio;
  if (dar != null && Number.isFinite(dar) && dar > 0) {
    out.displayAspectRatio = dar;
  }
  return out;
}

/**
 * 读侧：优先 `meta.headerImage`，否则回退 `cover_image` 仅 URL（无裁切，供历史数据）。
 */
export function resolveKnowledgeBaseHeaderImageFromRecord(
  input: { meta: unknown; coverImage: string | null | undefined },
  legacyDisplayAspectRatio: number,
): KnowledgeBaseMetaHeaderImage | null {
  const fromMeta = parseKnowledgeBaseMetaHeaderImageFromMeta(input.meta);
  if (fromMeta) return fromMeta;
  const legacy =
    typeof input.coverImage === 'string' && input.coverImage.trim() !== '' ? input.coverImage.trim() : null;
  if (!legacy) return null;
  return {
    src: legacy,
    cropNorm: null,
    displayAspectRatio: legacyDisplayAspectRatio,
  };
}

/** 合并写入 `meta`：`header === null` 或空 `src` 时删除 `headerImage` 键 */
export function mergeMetaWithHeaderImage(
  base: Record<string, unknown>,
  header: KnowledgeBaseMetaHeaderImage | null,
  defaultDisplayAspectRatio: number,
): Record<string, unknown> {
  const next = { ...base };
  if (header == null || !header.src.trim()) {
    delete next.headerImage;
    return next;
  }
  next.headerImage = normalizeMetaHeaderImageForSave(header, defaultDisplayAspectRatio);
  return next;
}

/** 写入后端前按 mode 去掉无关字段 */
export function normalizeMetaBackgroundForSave(bg: KnowledgeBaseMetaBackground): KnowledgeBaseMetaBackground {
  switch (bg.mode) {
    case 'none':
      return { mode: 'none' };
    case 'color':
      return { mode: 'color', color: bg.color ?? '' };
    case 'gradient': {
      const out: KnowledgeBaseMetaBackground = {
        mode: 'gradient',
        gradient: bg.gradient ?? '',
      };
      if (bg.imageFit != null) out.imageFit = bg.imageFit;
      if (bg.imagePosition != null) out.imagePosition = bg.imagePosition;
      return out;
    }
    case 'image': {
      const out: KnowledgeBaseMetaBackground = {
        mode: 'image',
        imageUrl: bg.imageUrl ?? '',
      };
      if (bg.imageFit != null) out.imageFit = bg.imageFit;
      if (bg.imagePosition != null) out.imagePosition = bg.imagePosition;
      return out;
    }
  }
}

function cssUrl(href: string): string {
  const escaped = href.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

function backgroundSizeFromFit(fit: KnowledgeBaseMetaBackgroundImageFit | undefined): string {
  switch (fit ?? 'cover') {
    case 'cover':
      return 'cover';
    case 'contain':
      return 'contain';
    case 'fill':
      return '100% 100%';
    case 'repeat':
      return 'auto';
    case 'no-repeat':
      return 'auto';
    default:
      return 'cover';
  }
}

function backgroundRepeatFromFit(fit: KnowledgeBaseMetaBackgroundImageFit | undefined): 'repeat' | 'no-repeat' {
  return fit === 'repeat' ? 'repeat' : 'no-repeat';
}

/**
 * 中栏根容器样式：与 `meta.background` 语义一致。
 * - `color` → 仅 `background-color`
 * - `gradient` → `background-image` 为渐变字符串
 * - `image` → `background-image: url(...)`
 */
export function metaBackgroundToMiddleColumnStyle(bg: KnowledgeBaseMetaBackground | null): CSSProperties {
  if (bg == null || bg.mode === 'none') {
    return { background: 'var(--background)' };
  }
  if (bg.mode === 'color') {
    const c = bg.color?.trim();
    if (!c) return { background: 'var(--background)' };
    return {
      backgroundColor: c,
      backgroundImage: 'none',
    };
  }
  if (bg.mode === 'gradient') {
    const g = bg.gradient?.trim();
    if (!g) return { background: 'var(--background)' };
    const fit = bg.imageFit ?? 'cover';
    const position = bg.imagePosition ?? 'center';
    return {
      backgroundColor: 'transparent',
      backgroundImage: g,
      backgroundSize: backgroundSizeFromFit(fit),
      backgroundPosition: position,
      backgroundRepeat: backgroundRepeatFromFit(fit),
    };
  }
  if (bg.mode === 'image') {
    const u = bg.imageUrl?.trim();
    if (!u) return { background: 'var(--background)' };
    const fit = bg.imageFit ?? 'cover';
    const position = bg.imagePosition ?? 'center';
    return {
      backgroundColor: 'transparent',
      backgroundImage: cssUrl(u),
      backgroundSize: backgroundSizeFromFit(fit),
      backgroundPosition: position,
      backgroundRepeat: backgroundRepeatFromFit(fit),
    };
  }
  return { background: 'var(--background)' };
}

/** 判断纯色色块是否与当前 `meta.background` 选中项一致（仅 `mode === "color"`） */
export function isMetaBackgroundSolidColor(
  bg: KnowledgeBaseMetaBackground | null,
  hex: string,
): boolean {
  if (bg == null || bg.mode !== 'color' || !bg.color) return false;
  return bg.color.trim().toLowerCase() === hex.trim().toLowerCase();
}
